/**
 * What each dashboard section is for, shown by the ⓘ next to its title (components/app/SectionInfo.tsx).
 * Card and Panel look their title up here, so a section gets its explanation wherever it appears.
 * Keep each answer short and practical: what it is, what the person gets out of it, what to do next.
 * A page can also pass `info` to Card for a section whose title isn't fixed text.
 */
export interface SectionHelp {
  what: string;
  why: string;
  how?: string;
}

export const SECTION_HELP: Record<string, SectionHelp> = {
  /* ---------- Site Audit → Sitelinks ---------- */
  "Sitelinks readiness": {
    what: "A 0-100 score for how clearly your site's structure shows Google its important pages: menu, clicks from the home page, internal links, WebSite markup, breadcrumbs, titles and addresses.",
    why: "Google picks sitelinks (the extra links under your result) by itself, from sites whose structure makes the key pages obvious. Nobody can switch them on, but a clear structure gives Google good choices.",
    how: "Start with \"Do these first\". Each checklist item has the steps, where to put it on WordPress, Shopify or other sites, and something to copy. Your next audit shows the new score.",
  },
  "Pages most likely to be chosen": {
    what: "The inner pages with the strongest signals in this audit: in the main menu, close to the home page, linked often, with a clear title.",
    why: "These are the pages Google is most likely to show as sitelinks. If a page you care about isn't here, its signals are weaker than you think.",
    how: "Make sure each one is in the main menu with a short label (shown on the right), linked from the home page, and has its own title.",
  },
  "How the score is made": {
    what: "The seven signals behind the Sitelinks readiness score, with the points each one earned and what the audit found.",
    why: "Shows exactly where the missing points are, so you fix the signal that is worth most first.",
    how: "Look for the bars that are short against their weight; their checklist items tell you what to change.",
  },
  "Main menu on the home page": {
    what: "The links Rankbist found in the home page's menu (<nav> or header) and footer, as search engines read them.",
    why: "The menu every page shares is the strongest signal of what matters on a site. A menu built only by JavaScript, or missing key pages, weakens it.",
    how: "Add any key page listed as missing, fix links shown in red, and keep the top level short.",
  },
  /* ---------- Brand Radar ---------- */
  "What to do next": {
    what: "The most useful actions from this check's AI answers, ordered by how much they can raise how often AI names you.",
    why: "You don't have to read every answer to know what to change: each step says why it matters and how to do it.",
    how: "Start with the first item. Each one links to the tool that does it.",
  },
  Leaderboard: {
    what: "Every brand you track, ranked by AI visibility: how often AI answers name it, cite its site, and how early it's named.",
    why: "Shows who AI recommends instead of you for your own questions, so you know who to study and overtake.",
    how: "Look at the brand above you: open Questions to read the answers that name it, and see Where to get mentioned for the sites behind them.",
  },
  "You and your competitors": {
    what: "The share of AI answers that mention each brand (light bar) and cite its website as a source (solid bar).",
    why: "Being cited sends visitors and trust; being mentioned puts you in the shortlist. Both can be raised.",
    how: "To be mentioned: answer your questions plainly on your site and get listed on the sites AI cites. To be cited: publish the clearest page for each question.",
  },
  "By AI engine": {
    what: "The same numbers for each AI assistant separately.",
    why: "Engines read different sources. A gap in one engine points to where to get listed.",
    how: "If one engine never names you, check its answers under Questions and the sites it cites.",
  },
  "How AI describes you": {
    what: "The tone of the answers that name you (positive, neutral or negative), with the exact sentences.",
    why: "AI repeats what the web says about you. A negative or vague description costs you customers before they reach your site.",
    how: "Correct wrong facts on your own pages first, then ask happy customers for reviews on the sites AI cites. If no answer names you yet, start with “What to do next”.",
  },
  "Brands AI recommends that you don't track": {
    what: "Brands that AI names in your answers but aren't on your competitor list.",
    why: "These are the brands AI sends your buyers to instead of you, often ones you didn't know about. Tracking them shows where they beat you and why.",
    how: "Press Track to follow one weekly. Write an honest “alternatives” or comparison page for the biggest ones: AI uses those pages to answer “X vs Y” and “best X” questions.",
  },
  "Where AI gets its answers": {
    what: "What kinds of sites the AI answers cite: your site, competitors, review sites, forums, blogs and so on.",
    why: "Tells you where to be present. If most sources are review and comparison sites, a listing there helps more than another blog post.",
    how: "Put your effort into the biggest kind of source first.",
  },
  "Where to get mentioned": {
    what: "Sites AI cites in answers that don't name you yet.",
    why: "Each one is a direct way into those answers: when it mentions you, AI can repeat it.",
    how: "Get a listing, a review or a guest article on the top sites. Review and comparison sites usually let you add your product yourself.",
  },
  "Your pages AI cites": {
    what: "The exact pages of your site that AI used as sources.",
    why: "These pages speak for you inside AI answers. Keeping them accurate keeps what AI says about you accurate.",
    how: "Keep them up to date. If none are cited, write one clear page per question you track (Article generator), with a short direct answer at the top.",
  },
  "Week by week": {
    what: "How often each brand was named, check by check, and your AI visibility score (dashed line).",
    why: "AI answers vary from week to week; the trend shows whether your work is paying off.",
  },
  Questions: {
    what: "Every question asked, with what each AI answered, who it named and which sites it cited.",
    why: "Reading the answers shows exactly why a competitor is picked, in the AI's own words.",
    how: "Open the Gaps filter first: questions where a competitor is named and you aren't.",
  },
  "Sites AI used as sources": {
    what: "All the sites the answers cited, most used first.",
    why: "The web pages AI trusts for your topic. Being on them is the quickest route into answers.",
  },

  /* ---------- Overview ---------- */
  "Today's focus": {
    what: "The few things that will move your scores most right now, from your latest checks, websites and alerts.",
    why: "Saves you deciding where to start: every item opens the page that fixes it.",
  },
  "Where issues come from": {
    what: "Which categories (keywords, readability, links…) your failed checks fall into, for the period you picked.",
    why: "If one category keeps failing, fixing that habit raises every future page.",
  },
  "Content categories": {
    what: "Your average content score in each category, and how many issues each one had.",
    why: "Shows your weakest area across all the pages you checked.",
  },
  Reports: {
    what: "Every check you ran in the period you picked, newest first.",
    why: "Open any of them to see its full report, AI fixes and share link.",
  },

  /* ---------- Websites, audits, indexing ---------- */
  "Fix these": {
    what: "The checks your home page failed in the latest daily check, with where to fix each one and how.",
    why: "These are the problems that most affect how Google reads and ranks your site.",
  },
  "What changed": {
    what: "What got better or worse since the previous daily check.",
    why: "Catches a broken change the day after it happens, not weeks later in traffic.",
  },
  "Google index": {
    what: "How many of your pages Google has indexed, from your own Search Console.",
    why: "A page Google hasn't indexed gets no search traffic at all.",
    how: "Open Indexing to see Google's reason for each missing page and the fix.",
  },
  "Your audits": {
    what: "Every Site Audit you ran, with its Site health and content average.",
    why: "Compare audits to see what got fixed and what broke since last time.",
  },
  "Scheduled audits": {
    what: "Audits that run by themselves every week or month.",
    why: "Your site is checked without you remembering to, and you're told what changed.",
  },
  "Why pages aren't indexed": {
    what: "Your pages that aren't in Google, grouped by Google's own reason.",
    why: "Each reason has one fix, so fixing a group fixes many pages at once.",
    how: "Start with the biggest group at the top.",
  },
  "Needs attention": {
    what: "Pages with an indexing problem right now.",
    why: "These are the pages losing search traffic because Google doesn't show them.",
    how: "Open one to see Google's reason and what to do.",
  },
  "Recently checked": {
    what: "The pages Rankbist checked in Google most recently, with their status.",
    why: "Confirms a fix worked: a page you fixed should turn “Indexed”.",
  },

  /* ---------- Rank Tracker & research ---------- */
  "Closest to the top": {
    what: "Searches where you rank 4th to 20th with the most impressions.",
    why: "Small improvements here pay off fastest: moving from 8th to 3rd can multiply clicks.",
    how: "Press Optimize to check the page for that search and follow the fixes.",
  },
  "All queries": {
    what: "Every search your site appeared for in Google, with position, clicks and impressions.",
    why: "Real data from your own Search Console, not estimates.",
  },
  "Link gap": {
    what: "Sites that link to your competitors but not to you.",
    why: "They already link to sites like yours, so they're the most likely to link to you too.",
    how: "Start with the strongest ones and offer something worth linking to.",
  },
  "Strongest referring domains": {
    what: "The strongest sites that link to this domain, leaving out big platforms (search engines, CDNs, app stores) that link to almost everyone.",
    why: "Links from strong sites count most for rankings, and these are sites you could actually ask.",
  },
  "Outreach list": {
    what: "The sites you want a link from, with the contact each one publishes, a ready email and where each request stands.",
    why: "Link building is follow-up: keeping track is what turns a list into links.",
    how: "Add sites from Link gap, click Find contact, send the email draft, then set the status to Contacted.",
  },

  /* ---------- Creator Studio ---------- */
  "Rising ideas in your niche": {
    what: "Phrases that several fast-growing videos from the last 14 days share in their titles.",
    why: "Each one is a video idea with proof that people are watching it right now.",
    how: "Open Research on an idea, then the SEO kit of the best video for its tags.",
  },
  "Beating their channel size": {
    what: "Videos with far more views than their channel has subscribers.",
    why: "The topic did the work, not the channel's size, so the topic can work for you too.",
  },
  "Popular in your category": {
    what: "The most-watched videos in your category and country right now.",
    why: "Shows the formats and topics your audience is watching today.",
  },
  "What people search for": {
    what: "Real search suggestions for your topics, as people type them.",
    why: "Titles that match how people search get found. “Not covered” are searches none of your titles answer yet.",
    how: "Use Questions for video titles and Not covered for your next videos.",
  },
  "Creator profiles": {
    what: "Your channels, each with its topics, language and country.",
    why: "Every Creator Studio page researches for the profile in use, so ideas and keywords fit that channel.",
    how: "Press “Use this profile” on the channel you're working on.",
  },

  /* ---------- Analytics ---------- */
  Traffic: {
    what: "Visitors, sessions (or visits) and conversions day by day for the period you picked, with markers for what happened.",
    why: "A drop or a jump next to a marker (an audit, an article, applied fixes, a Google update or your own note) shows what moved your traffic.",
    how: "Add a note whenever you change something big on the site, so you can see its effect later.",
  },
  "Where visitors come from": {
    what: "Your traffic by channel: search, direct, social, referral, AI assistants, email and paid.",
    why: "Shows which channels work, so you put your time where visitors actually come from.",
    how: "If organic search is small, open Search traffic to see which pages already rank and build on them.",
  },
  "Top sources": {
    what: "The individual sites and campaigns that sent visitors.",
    why: "A site that sends you visitors is worth a relationship: a guest post, a better listing or a partnership.",
  },
  Countries: {
    what: "Where your visitors are.",
    why: "If most visitors come from one country, write for it: its spelling, currency, examples and time zone.",
  },
  Devices: {
    what: "Visitors on mobile, desktop and tablet, and how many are new or returning.",
    why: "If most are on mobile, mobile speed and layout matter most. Run Lighthouse on mobile for your top pages.",
  },
  "Page performance": {
    what: "Every page with its visits and conversions next to its Google clicks, impressions, position, content score and speed.",
    why: "The flags show where a fix pays off most: seen on Google but rarely clicked, close to the top, losing visits, slow or low-scoring.",
    how: "Filter by “Needs work”, open a page and use its buttons to check it or run Lighthouse.",
  },
  "Search traffic": {
    what: "Visits from search engines over time, with Google's clicks and impressions from your Search Console.",
    why: "Tells you whether your SEO work is bringing more people from search, and when that changed.",
  },
  "Pages people land on from search": {
    what: "The pages search engines send visitors to, with the Google searches behind each one.",
    why: "These pages already work in search; improving them (and linking to them) is the fastest way to grow.",
    how: "Make sure each page answers its top searches clearly, then check it with the Content checker.",
  },
  "By event": {
    what: "Your conversions by name: sign-ups, purchases, contact forms, whatever you count.",
    why: "Shows which actions your visitors take, and which ones are rare.",
  },
  "By channel": {
    what: "Conversions and conversion rate for each channel.",
    why: "A channel with fewer visitors but a higher rate can be worth more than a big one. Spend effort where visitors convert.",
  },
  "By landing page": {
    what: "The pages converting visitors started on.",
    why: "These pages earn you customers. Keep them fast, accurate and easy to reach.",
  },
  "By source": {
    what: "Conversions and conversion rate for each site or campaign that sent visitors.",
    why: "Tells you which sources bring buyers, not just traffic.",
  },
  "From AI assistants": {
    what: "Conversions from visitors that ChatGPT, Perplexity, Gemini and other assistants sent you.",
    why: "Shows whether being named in AI answers brings real customers. Brand Radar shows how often you're named.",
  },
  "By assistant": {
    what: "Visits from each AI assistant, from your Google Analytics.",
    why: "Tells you which assistants already send people to you.",
  },
  "Pages they landed on": {
    what: "The pages AI assistants sent visitors to.",
    why: "These pages speak for you inside AI answers. Keep them accurate and up to date.",
  },
  Goals: {
    what: "Pages you count as a conversion, like a sign-up confirmation or an order's thank-you page, with how often they were reached.",
    why: "You see conversions without setting up events in Google Analytics or adding code.",
    how: "Add the page people see after they do what you want. With the Rankbist script, goals count from when you add them.",
  },
  Campaigns: {
    what: "Visits and conversions for every link you tagged with utm_campaign: emails, ads, social posts.",
    why: "Shows which campaigns bring customers, not just clicks, so you spend on the ones that work.",
    how: "Add ?utm_source=…&utm_medium=…&utm_campaign=… to the links you share.",
  },
  "Visits from AI assistants": {
    what: "Visits that came from AI assistants, counted by the Rankbist script.",
    why: "Shows whether AI answers send you visitors. Many arrive without saying so, so the real number is higher.",
  },

  /* ---------- Local SEO ---------- */
  "Business tasks": {
    what: "Jobs only the business owner can do, such as verifying the Google listing or adding photos, as step-by-step tasks.",
    why: "These steps decide whether you appear in local search and on Maps.",
  },

  /* ---------- Improvements round (action plan, client reports, alerts, account) ---------- */
  "Alerts to Slack & webhooks": {
    what: "Every notification from the bell (website down, index drops, traffic changes, finished reports) also sent to a Slack channel or your own system.",
    why: "The team sees problems where it already works, without opening Rankbist.",
    how: "Add a Slack Incoming Webhook URL or your endpoint, pick which notifications, then press Send test.",
  },
  "Read-only API": {
    what: "Read-only keys that let your own tools read your websites, audits, action plans, rankings and index status.",
    why: "Put the numbers in your client portal, spreadsheet or dashboard without copying them by hand.",
    how: "Create a key, copy it once, and send it as Authorization: Bearer rbk_ro_… to the /api/v1 addresses.",
  },
  "What your changes did": {
    what: "For each change on the traffic chart (an audit, an article, applied fixes, your notes, Google updates), visits in the 14 days after it next to the 14 days before.",
    why: "Shows which of your changes moved traffic, so you do more of what works.",
    how: "Read it as a hint: traffic moves for many reasons at once. Add a note on the chart whenever you change something on the site.",
  },
  "Competitor positions": {
    what: "Where you and up to three competitors appear in Google's top 30 for your keywords, checked every week, and whether Google's AI Overview showed and cited you.",
    why: "Search Console only shows your own site. This shows who beats you for the searches you want, and whether AI Overviews take the clicks.",
    how: "Open a keyword for Google's top 10 and the AI Overview's sources. Compare your page with the ones above you (Compare pages).",
  },
  "Action plan": {
    what: "Every problem the daily check, Site Audit, Indexing, Lighthouse and Rank Tracker found on this website, in one list.",
    why: "It's ordered by the traffic each problem puts at risk, using your own Search Console or Analytics numbers, so the first rows are worth the most.",
    how: "Open a row for why it matters, how to fix it and which pages. \"Copy for developer\" gives the whole list as text.",
  },
};

export function helpFor(title: unknown): SectionHelp | null {
  return typeof title === "string" ? SECTION_HELP[title] ?? null : null;
}
